import { UIMessage } from '@ai-sdk/react';
import { Tool } from '@procureai/agents';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Suggestion } from '../../Suggestion/Suggestion';
import { SuggestionList } from '../../SuggestionList/SuggestionList';
import { ChatLoadingIndicator } from '../indicators/ChatLoadingIndicator';
import { appConfig } from '~/common/config';
import { I18nNamespace } from '~/common/types';
import { useToolCall } from '~/hooks';

export type MessageSuggestionsPartProps = {
  message: UIMessage;

  isStreaming?: boolean;
  isLastMessage: boolean;
};

export const MessageSuggestionsPart = (props: MessageSuggestionsPartProps): ReactNode => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'Chat.tools.common' });
  const { message, isLastMessage, isStreaming } = props;
  const isEnabled = appConfig.features.enableMessageSuggestions;

  const [suggestions, setSuggestions] = useState<{ label: string; message: string }[]>();
  const { mutate: getSuggestions, isLoading: isGenerating } = useToolCall(Tool.userMessageSuggestions);
  const hasFetchedSuggestions = useRef(false);

  const fetchSuggestions = useCallback(async () => {
    const { rawSuggestions } = await getSuggestions({
      id: message.id,
    });
    setSuggestions(rawSuggestions);
  }, [getSuggestions, message.id]);

  useEffect(() => {
    if (!isEnabled) return;

    if (isLastMessage && !isStreaming && message.id && !isGenerating && !hasFetchedSuggestions.current) {
      fetchSuggestions();
      hasFetchedSuggestions.current = true;
    }
  }, [isLastMessage, isStreaming, message.id, isGenerating, fetchSuggestions, isEnabled]);

  if (!isEnabled) {
    return null;
  }

  // show loading indicator
  if (isGenerating) {
    return <ChatLoadingIndicator message={t('generate', { context: 'suggestions' })} />;
  }

  // only show suggestions for the last message and when not streaming
  if (!isLastMessage || !suggestions || isStreaming) {
    return null;
  }

  return (
    <SuggestionList>
      {suggestions.map(({ message, label }, index) => (
        <Suggestion key={`suggestion-${index}`} message={message}>
          {label}
        </Suggestion>
      ))}
    </SuggestionList>
  );
};
